import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { imageBlockPatchFromEmailBuilderMediaItem } from '../operations/emailBuilderMediaRef';
import { imageBlockPatchForMediaChange } from '../operations/imageBlockMediaChange';
import { EmailBuilderMediaGalleryModal } from '../shell/panel/blockSettings/EmailBuilderMediaGalleryModal';
import { renderImageBlock } from '../render/blocks/image';
import {
  ImageResizeHandles,
  type ImageBoxRect,
} from './ImageResizeHandles';
import { useEditorFacility } from './EditorFacilityContext';
import { resolveBlockLayoutContext } from '../domain/block-width';
import { selectionEquals } from '../domain/selection/equals';
import { resolveMediaRef, isFacilityLogoMediaRef } from '../domain/media';
import {
  selectDocument,
  selectEmailSettings,
  selectSelection,
} from '../domain/selectors';
import {
  getEditorStoreState,
  useEditorActions,
  useEditorStore,
} from '../domain/store';
import { defaultWidthPxForImageBlock } from '../domain/facility-logo-sizing';
import { isImageDefaultWidth, storedImageWidthPx } from '../domain/image-width';
import type { ImageBlock } from '../domain/types';
import { findImageMeasureTarget } from './image-measure';

type EditableImageProps = {
  rowId: string;
  columnId: string;
  block: ImageBlock;
};

function measureImageBox(root: HTMLElement, target: Element): ImageBoxRect {
  const rootRect = root.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  return {
    left: targetRect.left - rootRect.left,
    top: targetRect.top - rootRect.top,
    width: targetRect.width,
    height: targetRect.height,
  };
}

function imageBoxEquals(a: ImageBoxRect | null, b: ImageBoxRect): boolean {
  return (
    a !== null &&
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height
  );
}

export function EditableImage({ rowId, columnId, block }: EditableImageProps) {
  const { t } = useTranslation();
  const facility = useEditorFacility();
  const document = useEditorStore(selectDocument);
  const settings = useEditorStore(selectEmailSettings);
  const selection = useEditorStore(selectSelection);
  const { updateBlock } = useEditorActions();
  const rootRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const imageBoxRafRef = useRef(0);
  const pickerPointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const pickerArmedRef = useRef(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [imageBox, setImageBox] = useState<ImageBoxRect | null>(null);
  const { contentMaxWidth } = resolveBlockLayoutContext(
    document,
    rowId,
    columnId,
    block.padding,
  );

  const currentSelection = {
    kind: 'block' as const,
    rowId,
    columnId,
    blockId: block.id,
  };
  const selected = selectionEquals(selection, currentSelection);
  const src = resolveMediaRef(block.media, facility);
  const usesFacilityLogo = isFacilityLogoMediaRef(block.media);
  const srcBroken = brokenSrc !== null && brokenSrc === src;
  const showFacilityLogoFallback = usesFacilityLogo && (!src || srcBroken);
  const isEmpty = !src || showFacilityLogoFallback;
  const isFillColumn = block.heightMode === 'fillColumn';
  const imageBytesLoaded = loadedSrc === src && src.length > 0;
  const isEmptyRef = useRef(isEmpty);
  const selectedRef = useRef(selected);
  const mediaOpenRef = useRef(mediaOpen);

  const handleEditorImageBytesLoaded = useCallback(() => {
    setLoadedSrc(src);
  }, [src]);

  useEffect(() => {
    if (!usesFacilityLogo || !src) {
      return;
    }

    const probe = new Image();
    probe.onload = () => setBrokenSrc(null);
    probe.onerror = () => setBrokenSrc(src);
    probe.src = src;
  }, [src, usesFacilityLogo, block.media]);

  useEffect(() => {
    isEmptyRef.current = isEmpty;
    selectedRef.current = selected;
    mediaOpenRef.current = mediaOpen;
  });

  useEffect(() => {
    if (!selected) {
      pickerArmedRef.current = false;
    }
  }, [selected]);

  useEffect(() => {
    if (!isEmpty) {
      pickerArmedRef.current = false;
    }
  }, [isEmpty]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !isEmpty) {
      return;
    }

    const isThisBlockSelected = () =>
      selectionEquals(getEditorStoreState().selection, currentSelection);

    const onMouseDown = (event: MouseEvent) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        !isEmptyRef.current ||
        !isThisBlockSelected() ||
        !pickerArmedRef.current
      ) {
        return;
      }
      pickerPointerStartRef.current = { x: event.clientX, y: event.clientY };
    };

    const onMouseUp = (event: MouseEvent) => {
      if (
        event.button !== 0 ||
        !isEmptyRef.current ||
        !isThisBlockSelected() ||
        mediaOpenRef.current
      ) {
        return;
      }
      const start = pickerPointerStartRef.current;
      pickerPointerStartRef.current = null;
      if (start) {
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (dx * dx + dy * dy < 64) {
          event.stopPropagation();
          pickerArmedRef.current = false;
          setMediaOpen(true);
          return;
        }
      }
      pickerArmedRef.current = true;
    };

    const onDoubleClick = (event: MouseEvent) => {
      if (!isEmptyRef.current) {
        return;
      }
      event.stopPropagation();
      setMediaOpen(true);
    };

    root.addEventListener('mousedown', onMouseDown);
    root.addEventListener('mouseup', onMouseUp);
    root.addEventListener('dblclick', onDoubleClick);

    return () => {
      root.removeEventListener('mousedown', onMouseDown);
      root.removeEventListener('mouseup', onMouseUp);
      root.removeEventListener('dblclick', onDoubleClick);
    };
  }, [isEmpty]);

  useEffect(() => {
    if (!src) {
      setNaturalSize(null);
      return;
    }
    if (block.intrinsicSize != null) {
      setNaturalSize({
        width: block.intrinsicSize.width,
        height: block.intrinsicSize.height,
      });
      return;
    }
    setNaturalSize(null);
    const img = new Image();
    img.onload = () => {
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = src;
  }, [src, block.intrinsicSize]);

  useEffect(() => {
    if (block.media === null) {
      return;
    }

    const intrinsicSize = block.intrinsicSize ?? naturalSize;
    if (
      !intrinsicSize ||
      intrinsicSize.width <= 0 ||
      intrinsicSize.height <= 0
    ) {
      return;
    }

    const layoutWidth =
      contentMaxWidth > 0 ? contentMaxWidth : settings.contentWidth;
    const atDefaultWidth = isImageDefaultWidth(block);
    const patch: Partial<ImageBlock> = {};

    if (block.intrinsicSize == null && naturalSize != null) {
      patch.intrinsicSize = { ...naturalSize };
    }

    if (atDefaultWidth) {
      const computedWidthPx = defaultWidthPxForImageBlock(
        block,
        intrinsicSize,
        layoutWidth,
      );
      if (
        computedWidthPx !== null &&
        computedWidthPx !== storedImageWidthPx(block)
      ) {
        patch.widthPx = computedWidthPx;
      }
    }

    if (Object.keys(patch).length === 0) {
      return;
    }

    updateBlock(rowId, columnId, block.id, patch, { history: false });
  }, [
    block.media,
    block.intrinsicSize,
    block.facilityLogoSizing,
    naturalSize,
    block.widthPx,
    block.id,
    contentMaxWidth,
    settings.contentWidth,
    rowId,
    columnId,
    updateBlock,
  ]);

  useEffect(() => {
    const root = rootRef.current;
    const content = root?.firstElementChild;
    if (!root || !(content instanceof HTMLElement) || !selected || !src) {
      setImageBox(null);
      return;
    }

    const scheduleImageBoxUpdate = () => {
      if (imageBoxRafRef.current !== 0) {
        return;
      }
      imageBoxRafRef.current = requestAnimationFrame(() => {
        imageBoxRafRef.current = 0;
        const target = findImageMeasureTarget(content);
        if (!target) {
          setImageBox(null);
          return;
        }
        const nextBox = measureImageBox(root, target);
        setImageBox(prev =>
          imageBoxEquals(prev, nextBox) ? prev : nextBox,
        );
      });
    };

    scheduleImageBoxUpdate();
    const observer = new ResizeObserver(scheduleImageBoxUpdate);
    observer.observe(root);
    const target = findImageMeasureTarget(content);
    if (target instanceof Element) {
      observer.observe(target);
    }

    return () => {
      observer.disconnect();
      if (imageBoxRafRef.current !== 0) {
        cancelAnimationFrame(imageBoxRafRef.current);
        imageBoxRafRef.current = 0;
      }
    };
  }, [
    selected,
    src,
    block.widthPx,
    block.align,
    block.heightMode,
    block.heightPx,
    block.padding,
    block.media,
    block.objectFit,
    block.fullWidthMobile,
  ]);

  return (
    <div
      ref={rootRef}
      className={"email-builder-image-resize-root"}
      data-image-block="true"
      data-fill-column={isFillColumn ? 'true' : 'false'}
      data-auto-width={
        block.heightMode === 'auto' && !isEmpty ? 'true' : 'false'
      }
      data-empty-selected={isEmpty && selected ? 'true' : undefined}
      role={isEmpty && selected ? 'button' : undefined}
      title={isEmpty && selected ? t('Click to choose image') : undefined}
      aria-label={isEmpty && selected ? t('Choose image') : undefined}>
      <div
        ref={node => {
          imgRef.current =
            node?.querySelector('img.email-img-bg-fallback, img[alt]') ?? null;
        }}>
        {renderImageBlock({
          block,
          facility,
          textTheme: settings,
          mergeMode: 'raw',
          editorMode: true,
          editorSuppressSrc: showFacilityLogoFallback,
          editorImageBytesLoaded: isEmpty ? true : imageBytesLoaded,
          onEditorImageBytesLoaded: handleEditorImageBytesLoaded,
          contentMaxWidth,
        })}
      </div>
      {selected && src && imageBox ? (
        <ImageResizeHandles
          block={block}
          contentMaxWidth={contentMaxWidth}
          imageBox={imageBox}
          naturalSize={naturalSize}
          onPatch={patch => updateBlock(rowId, columnId, block.id, patch)}
        />
      ) : null}
      {mediaOpen ? (
        <EmailBuilderMediaGalleryModal
          mode="add"
          isOpen
          includeFacilityLogo
          linkedImageHint={block.href.trim().length > 0}
          onClose={() => setMediaOpen(false)}
          onConfirm={({ media, alt }) => {
            const layoutWidth =
              contentMaxWidth > 0 ? contentMaxWidth : settings.contentWidth;
            updateBlock(
              rowId,
              columnId,
              block.id,
              imageBlockPatchForMediaChange({
                block,
                action: 'add',
                contentMaxWidth: layoutWidth,
                mediaPatch: imageBlockPatchFromEmailBuilderMediaItem(media),
                alt,
              }),
            );
          }}
        />
      ) : null}
    </div>
  );
}
